import { useEffect, useRef, useState } from 'react'
import { FileText, Upload, Trash2, Download, Loader2, FileX2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { toast } from '@/components/ui/Toast'
import { dateTimeBR } from '@/lib/format'
import type { DocItem, DocService } from '@/services/modules/DocumentsService'

function humanSize(bytes?: number | null): string {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  /** Service da entidade (standardDocuments / providerContracts). */
  service: DocService
  /** Em cadastro novo (sem id), o gerenciamento fica indisponível. */
  enabled: boolean
  title?: string
  description?: string
  /** Tipos aceitos no input (ex.: '.pdf,image/*'). */
  accept?: string
  /** Só consulta: esconde o envio e a remoção (usuário sem permissão de escrita). */
  readOnly?: boolean
}

export function DocumentsManager({
  service,
  enabled,
  title = 'Documentos',
  description = 'Anexe e gerencie os arquivos deste cadastro.',
  accept,
  readOnly = false,
}: Props) {
  const [items, setItems] = useState<DocItem[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [signedAt, setSignedAt] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = () => {
    if (!enabled) return
    setLoading(true)
    service.list()
      .then(setItems)
      .catch(() => toast.error('Não foi possível carregar os documentos.'))
      .finally(() => setLoading(false))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [enabled])

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await service.upload(file, { signedAt: signedAt || undefined })
      toast.success({ title: 'Documento enviado', description: file.name })
      setSignedAt('')
      load()
    } catch {
      toast.error('Falha ao enviar o documento.')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function remove(item: DocItem) {
    if (!window.confirm(`Remover "${item.name}"? Esta ação não pode ser desfeita.`)) return
    setDeletingId(item.id)
    try {
      await service.remove(item.id)
      toast.success('Documento removido.')
      setItems((prev) => prev.filter((d) => d.id !== item.id))
    } catch {
      toast.error('Falha ao remover o documento.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        <p className="text-xs text-gray-500">{description}</p>
      </div>

      {!enabled ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          Salve o cadastro primeiro para anexar documentos.
        </div>
      ) : (
        <>
          {/* Upload */}
          {!readOnly && <div className="flex flex-wrap items-end gap-3 rounded-md border border-gray-200 bg-gray-50/60 p-4">
            {service.hasSignedDate && (
              <div className="w-44">
                <Input
                  label="Data de assinatura"
                  type="date"
                  value={signedAt}
                  onChange={(e) => setSignedAt(e.target.value)}
                />
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={onPick}
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              loading={uploading}
              iconLeft={<Upload size={15} />}
            >
              {uploading ? 'Enviando' : 'Enviar arquivo'}
            </Button>
            <span className="text-xs text-gray-400">Até 20 MB.</span>
          </div>}

          {/* Lista */}
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-gray-300 py-10 text-center">
              <FileX2 size={22} className="mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">Nenhum documento anexado.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {items.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-3.5 py-2.5">
                  <FileText size={18} className="shrink-0 text-brand-500" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-gray-900">{d.name}</p>
                    <p className="break-words text-xs text-gray-500">
                      {d.signedAt ? `Assinado em ${dateTimeBR(d.signedAt)} · ` : ''}
                      {d.createdAt ? `Enviado em ${dateTimeBR(d.createdAt)}` : ''}
                      {humanSize(d.size) ? ` · ${humanSize(d.size)}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {d.url && (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-brand-600"
                        title="Abrir / baixar"
                      >
                        <Download size={15} />
                      </a>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => remove(d)}
                        disabled={deletingId === d.id}
                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                        title="Remover"
                      >
                        {deletingId === d.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

export default DocumentsManager
